import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import React, {
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AutoComplete,
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  Rate,
  Select,
  Spin,
  Popconfirm,
  Dropdown,
  Menu,
  Modal,
  Switch,
} from "antd";

import axios from "axios";
import BreadCrumb from "../Layouts/Breadcrumb";
import AddBtn from "../common/AddBtn";
import { responseNotification } from "../../utils/notify";
import Loader from "../common/Loader";
import Pagination from "../common/Pagination";
import { getPage, getParamValue } from "../../utils";
import { useLocation, useNavigate } from "react-router";
import styles from "../../styles/tailwind/List.module.css";
import { useDispatch, useSelector } from "react-redux";
import LoaderFull from "../common/LoaderFull";
import { debounce } from "lodash";
import classNames from "classnames";
import { LOGIN_SUCCESS } from "../../redux/auth/authType";
import { ShopStatus } from "../../utils";

const { confirm } = Modal;

const ShopList = (): ReactElement => {
  const { token, merchantId } = useSelector(
    (state) => (state as any)?.authReducer
  );

  const [type, setType] = useState("JC_COMMERCE");
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const loc = useLocation();
  const page = getParamValue(loc.search, "page");

  const [neighborhoodId, setNeighborhoodId] = useState("");
  const [limit, setLimit] = useState(10);
  const [key, setKey] = useState("");
  const [showSearch, setShowSearch] = useState(true);
  const [isActive, setIsActive] = useState<any>(undefined);

  // ✅ FIX: Popconfirm control will use shop.id (NOT mobileNumber)
  const [visiblePopconfirmId, setVisiblePopconfirmId] = useState<string | null>(
    null
  );

  // ✅ Separate loading states (avoid mixing)
  const [switchingShopId, setSwitchingShopId] = useState<string | null>(null);
  const [updatingShopId, setUpdatingShopId] = useState<string | null>(null);

  const [serviceOptions, setServiceOptions] = useState<any>({
    list: [],
    loading: false,
  });

  const [shopData, setShopData] = useState<any>({
    loading: false,
    data: null,
  });

  const [neighborhoodOptions, setNeighborhoodOptions] = useState({
    loading: false,
    list: [],
  });

  const [shopOptions, setShopOptions] = useState({
    loading: false,
    list: [],
  });

  const isDeleted = "false";

  const isBusy = !!switchingShopId || !!updatingShopId;

  const showPopconfirm = (shopId: string) => {
    setVisiblePopconfirmId(shopId);
  };

  const handleCancel = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setVisiblePopconfirmId(null);
  };

  const getShops = useCallback(async () => {
    setShopData({ loading: true, data: null });
    const encodedUrl = `${process.env.REACT_APP_CATALOG_READER_API}`;

    axios
      .get(
        `${encodedUrl}/merchant/all-shops-by-merchantId?merchantId=${merchantId}` +
          (type ? `&type=${type}` : ``) +
          (neighborhoodId ? `&neighborhoodId=${neighborhoodId}` : ``) +
          (key ? `&key=${key}` : ``) +
          (isActive !== undefined && isActive !== "" ? `&isActive=${isActive}` : ``) +
          (page
            ? `&page=${
                page == shopData.data?.currentPageNumber ? 0 : page || 0
              }`
            : ``) +
          (limit ? `&limit=${limit}` : ``),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        setShopData({ loading: false, data: res.data });
      })
      .catch((err) => {
        setShopData({ loading: false, data: [] });
        console.error("Type: Error", err);
      });
  }, [limit, page, merchantId, type, neighborhoodId, key, isActive, token]);

  const getShopOptions = useCallback(
    async (key: any) => {
      setShopOptions({ loading: true, list: [] });
      const encodedUri = `${process.env.REACT_APP_CATALOG_READER_API}`;

      return axios
        .get(
          `${encodedUri}/shop/admin/search?type=${type}&page=0&limit=20` +
            (merchantId ? `&merchantId=${merchantId}` : ``) +
            (key ? `&key=${key}` : ``),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          setShopOptions({
            loading: false,
            list: res.data?.shops?.map((shop: { name: any }) => ({
              label: shop.name,
              value: shop.name,
            })),
          });
        })
        .catch((err) => {
          setShopOptions({ loading: false, list: [] });
          console.error("Shops: Error", err);
        });
    },
    [type, merchantId, token]
  );

  const fetchShopDetails = useCallback(
    (res: any) => {
      if (!res) return;

      axios
        .get(
          `${process.env.REACT_APP_CATALOG_READER_API}/merchant-shop/my-shop-details?shopId=${res?.shopId}`,
          {
            headers: {
              Authorization: `Bearer ${res?.token}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((data) => {
          if (data.status === 200) {
            dispatch({
              type: LOGIN_SUCCESS,
              payload: {
                token: res?.token,
                userType: res?.usertype || "MERCHANT",
                shopInfo: data?.data?.shop,
                shopId: data?.data?.shop?.id,
                iat: new Date(),
                type: data?.data?.shop?.type || "JC_COMMERCE",
              },
            });
            navigate("/");
          } else {
            responseNotification(data.statusText || "something went wrong", "error");
          }
        })
        .catch((err) => console.log(err));
    },
    [dispatch, navigate]
  );

  const onSetDefault = async (shopId: any) => {
    if (!shopId) return;

    setSwitchingShopId(shopId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_API}/merchant/switch-to-shop-v2`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ shopId }),
        }
      );

      const json = await res.json();

      if (json.statusCode === 200) {
        responseNotification(json.message, "success");
        setVisiblePopconfirmId(null);
        fetchShopDetails(json);
      } else if (json.status === 500) {
        responseNotification("Internal server error", "error");
      } else {
        responseNotification(json.message || "something wrong", "warning");
      }
    } catch (err) {
      responseNotification(`Internal server error ${err}`, "error");
      console.error("err", err);
    } finally {
      setSwitchingShopId(null);
    }
  };

  const onShopActiveInactive = async (id?: any, val?: any) => {
    if (!id) return;

    setUpdatingShopId(id);
    try {
      const res = await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/shop`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, isActive: val }),
      });

      const json = await res.json();

      if (json.statusCode === 200) {
        responseNotification("Status Updated Successfully", "success");
        getShops();
      } else if (json.status === 500) {
        responseNotification("Internal server error", "error");
      } else {
        responseNotification(json.message || "something wrong", "warning");
      }
    } catch (err) {
      responseNotification(`Internal server error ${err}`, "error");
      console.error("err", err);
    } finally {
      setUpdatingShopId(null);
    }
  };

  const onStatusChange = async (id?: any, status?: string) => {
    if (!id) return;

    setUpdatingShopId(id);
    try {
      const res = await fetch(`${process.env.REACT_APP_CATALOG_WRITER_API}/shop`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, status }),
      });

      const json = await res.json();

      if (json.statusCode === 200) {
        responseNotification("Status Updated Successfully", "success");
        getShops();
      } else if (json.status === 500) {
        responseNotification("Internal server error", "error");
      } else {
        responseNotification(json.message || "something wrong", "warning");
      }
    } catch (err) {
      responseNotification(`Internal server error ${err}`, "error");
      console.error("err", err);
    } finally {
      setUpdatingShopId(null);
    }
  };

  function showConfirm(id: any, status: any) {
    confirm({
      title: `Do you Want to change Status "${status}"?`,
      icon: <ExclamationCircleOutlined />,
      onOk() {
        onStatusChange(id, status);
      },
      onCancel() {},
      okText: "Change Status",
    });
  }

  const getMyAllService = useCallback(async () => {
    setServiceOptions({ loading: true, list: [] });

    const res = await axios.get(
      `${process.env.REACT_APP_CATALOG_READER_API}/merchant/my-all-service-types`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setServiceOptions({
      loading: false,
      list: res?.data?.types?.map((service: any) => ({
        value: service?.type,
        label: service?.type,
      })),
    });
  }, [token]);

  const getNeighborhoodOptions = useCallback(
    async (neighborhoodKey: any) => {
      setNeighborhoodOptions({ loading: true, list: [] });
      const encodedUri = `${process.env.REACT_APP_CATALOG_READER_API}`;

      return axios
        .get(
          `${encodedUri}/neighborhood/all?type=${type}&page=0&limit=20` +
            (neighborhoodKey ? `&key=${neighborhoodKey}` : ``),
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          setNeighborhoodOptions({
            loading: false,
            list: res.data?.neighborhoods?.map((n: any) => ({
              label: n.name,
              value: n.id,
            })),
          });
        })
        .catch(() => setNeighborhoodOptions({ loading: false, list: [] }));
    },
    [type, token]
  );

  const fetchRef = useRef(0);
  const handleSearch = useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      if (fetchId !== fetchRef.current) return;

      if (!value) return;

      if (field === "shop") getShopOptions(value);
      if (field === "neighborhood" || field === "negihborhood")
        getNeighborhoodOptions(value);
    };

    return debounce(loadOptions, 800);
  }, [getShopOptions, getNeighborhoodOptions]);

  useEffect(() => {
    getShops();
    getMyAllService();
    getNeighborhoodOptions("");
  }, [getShops, getMyAllService, getNeighborhoodOptions]);

  const reseAllFieldData = () => {
    form.resetFields();
    setKey("");
    setIsActive(undefined);
    setNeighborhoodId("");
  };

  return (
    <>
      <BreadCrumb
        title={type == "JC_FOOD" ? "Restaurant List" : "Shop List"}
        subTitle={`${
          shopData?.data?.totalElements || shopData?.data?.shops?.length || 0
        } Shop(s)`}
        extra={[
          <Button
            type="dashed"
            shape="circle"
            onClick={() => setShowSearch(!showSearch)}
            key={1}
          >
            <SearchOutlined />
          </Button>,
          <AddBtn
            onClick={() => {
              navigate("/shops/create");
            }}
          />,
        ]}
      />

      {showSearch && (
        <div className={styles?.searchBox}>
          <Form layout="inline" form={form} className={styles.formInline}>
            <Form.Item name="type" initialValue={type}>
              <Select
                value={type}
                options={serviceOptions.list}
                loading={serviceOptions.loading}
                placeholder="Select Service Type"
                onChange={(val) => {
                  setType(val);
                  form.setFieldsValue({ type: val });
                }}
              />
            </Form.Item>

            <Form.Item name="negihborhood_search">
              <AutoComplete
                onSearch={(e) => handleSearch(e, "neighborhood")}
                onSelect={(val) => setNeighborhoodId(val?.toString())}
                options={neighborhoodOptions?.list}
                defaultActiveFirstOption={false}
                notFoundContent={
                  neighborhoodOptions?.loading ? <Spin size="small" /> : null
                }
                style={{ width: 300 }}
              >
                <Input.Search
                  size="large"
                  placeholder="Search by Neighborhood"
                  onSearch={(val) => setNeighborhoodId(val)}
                  enterButton
                  loading={neighborhoodOptions.loading}
                  maxLength={11}
                />
              </AutoComplete>
            </Form.Item>

            <Form.Item name="search">
              <AutoComplete
                style={{ width: 250 }}
                onSearch={(e) => handleSearch(e, "shop")}
                onSelect={(val: { toString: () => SetStateAction<string> }) =>
                  setKey(val.toString())
                }
                options={shopOptions?.list}
                defaultActiveFirstOption={false}
                notFoundContent={shopOptions?.loading ? <Spin size="small" /> : null}
              >
                <Input.Search
                  placeholder="Search by Name"
                  onSearch={(val) => setKey(val)}
                  enterButton
                  loading={shopOptions.loading}
                />
              </AutoComplete>
            </Form.Item>

            <Form.Item
              name="shop_search"
              initialValue={
                isActive == "true"
                  ? "ACTIVE"
                  : isActive == "false"
                  ? "INACTIVE"
                  : "ALL"
              }
            >
              <Select
                showSearch
                placeholder="Status"
                onChange={(val) => setIsActive(val as string)}
              >
                <Select.Option value={undefined}>ALL</Select.Option>
                <Select.Option value={"true"} title="ACTIVE">
                  ACTIVE
                </Select.Option>
                <Select.Option value={"false"} title="INACTIVE">
                  INACTIVE
                </Select.Option>
              </Select>
            </Form.Item>
          </Form>

          <Button
            type="primary"
            danger
            size="large"
            htmlType="reset"
            onClick={reseAllFieldData}
          >
            Reset
          </Button>
        </div>
      )}

      <div className={styles.contentWrapper}>
        <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
            <div className={showSearch ? `content-body-withSearch` : `content-body`}>
              {isBusy && <LoaderFull />}

              {shopData?.loading ? (
                <Loader />
              ) : (
                <table className={styles.mainTable}>
                  <thead className="bg-white border-b">
                    <tr>
                      <th scope="col">Shop</th>
                      <th scope="col">Contact Number</th>
                      <th scope="col">Ratings</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {shopData?.data?.shops?.length ? (
                      shopData?.data?.shops?.map((shop: any, index: any) => (
                        <tr className="border-t hover:bg-gray-100 cursor-pointer" key={index}>
                          <td className="flex gap-2 items-center">
                            <Avatar size={45} src={shop?.logo} className="border" />
                            <span className="font-medium text-gray-500 ml-2 flex flex-col">
                              {shop?.name}
                              <span>
                                {type == "JC_FOOD" && (
                                  <div className="flex">
                                    <span
                                      className={classNames(
                                        `text-xs font-light pl-1 pr-2 py-0.5 leading-3 rounded-full border col-auto flex items-center gap-1`,
                                        shop?.status == "Close"
                                          ? `bg-red-100 text-red-600`
                                          : shop?.status == "Busy"
                                          ? `bg-yellow-200 text-yellow-600`
                                          : `bg-green-100 text-green-600`
                                      )}
                                    >
                                      <ClockCircleOutlined />
                                      <small>{shop?.status?.toUpperCase()}</small>
                                    </span>
                                  </div>
                                )}
                              </span>
                            </span>
                          </td>

                          <td>
                            {shop?.mobileNumber?.replace("+88", "") ||
                              shop?.contactNumber?.replace("+88", "")}
                          </td>

                          <td>
                            <Rate value={shop?.rating || 1} disabled />{" "}
                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 dark:text-blue-800 ml-3">
                              {shop?.numberOfRating || 0}
                            </span>
                          </td>

                          <td>
                            {type == "JC_FOOD" ? (
                              <Dropdown.Button
                                overlay={
                                  <Menu
                                    onClick={(val) => showConfirm(shop?.id, val?.key)}
                                  >
                                    {Object?.values(ShopStatus)?.map((status, i) => (
                                      <Menu.Item key={status}>{status}</Menu.Item>
                                    ))}
                                  </Menu>
                                }
                              >
                                {shop?.status?.split("_").join(" ") || "Status"}
                              </Dropdown.Button>
                            ) : (
                              <Switch
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                checked={!!shop?.isActive} // ✅ controlled (no defaultChecked)
                                onChange={(val: any, e: any) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onShopActiveInactive(shop?.id, val);
                                }}
                                className="mr-1"
                                loading={updatingShopId === shop?.id}
                              />
                            )}
                          </td>

                          <td>
                            <div className="flex flex-grow gap-2">
                              <Popconfirm
                                placement="left"
                                title="Do you want to login this store?"
                                visible={shop?.id === visiblePopconfirmId}
                                open={shop?.id === visiblePopconfirmId} // ✅ supports newer antd too
                                onConfirm={(e: any) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onSetDefault(shop?.id);
                                }}
                                okButtonProps={{
                                  loading: switchingShopId === shop?.id,
                                  className: "bg-blue-400",
                                  shape: "round",
                                }}
                                cancelButtonProps={{
                                  danger: true,
                                  type: "primary",
                                  shape: "round",
                                }}
                                okText="Yes"
                                cancelText="Not now"
                                onCancel={handleCancel}
                              >
                                <Button
                                  className="bg-white"
                                  type="dashed"
                                  shape="round"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    showPopconfirm(shop?.id); // ✅ use shop.id
                                  }}
                                >
                                  Switch to Shop
                                </Button>
                              </Popconfirm>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>
                          <Empty />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <Pagination {...shopData?.data} limit={limit} page={getPage(loc.search)} />
      </div>
    </>
  );
};

export default ShopList;